import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import LegCard from './LegCard';
import ProductSection from './ProductSection';
import DeliverySection from './DeliverySection';
import { usePricingApi } from '../../hooks/usePricingApi';
import { useTheme } from '../../context/ThemeContext';
import { calcDefaultDeliveryDate, calcNextDeliveryDate } from '../../utils/dates';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useFormStore } from '../../stores/formData';

const ShippingLegForm = ({ 
  legIndex,
  defaultExpanded = true,
  originRef,
  destinationRef
}) => { 
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { theme } = useTheme();
  const [fetchingPricing, setFetchingPricing] = useState(false);

  const helpers = useFormStore((state) => state.helpers);
  const actions = useFormStore((state) => state.actions);
  const pricing = useFormStore((state) => state.formData.legs[legIndex].pricing);
  const currentLeg = useFormStore((state) => state.formData.legs[legIndex]);
  const origin = useFormStore((state) => state.formData.legs[legIndex]?.fromAddress);
  const destination = useFormStore((state) => state.formData.legs[legIndex]?.toAddress);

  const isMountedRef = useRef(false);

  const { 
    getDefaultPricing,
    loading: pricingLoading,
    error: pricingError,
    pricingData 
  } = usePricingApi();

  // Memoize addresses to prevent unnecessary reference changes
  const memoizedOrigin = useMemo(() => origin, [
    origin?.place_id,
    origin?.cruisePort?.id
  ]);
  
  const memoizedDestination = useMemo(() => destination, [
    destination?.place_id,
    destination?.cruisePort?.id
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Use memoized addresses in useEffect to prevent unnecessary pricing calls
  useEffect(() => {
    if(fetchingPricing) return;
     
    const fetchPricing = async () => {

      const originPlaceId = memoizedOrigin?.place_id || memoizedOrigin?.place?.id || 
        memoizedOrigin?.google_place_id;
      
      const destinationPlaceId = memoizedDestination?.place_id || memoizedDestination?.place?.id || 
        memoizedDestination?.google_place_id;

      if (originPlaceId && destinationPlaceId && (
        pricing?.originPlaceId !== originPlaceId ||
        pricing?.destinationPlaceId !== destinationPlaceId
      )) {

        const pricingParams = {
          origin: {
            id: originPlaceId,
            type: 'GooglePlace'
          },
          destination: {
            id: destinationPlaceId,
            type: 'GooglePlace'
          },
          brand_id: window.gon?.current_brand || 'shipplay',
          partner_id: window.gon.partner_id,
          direction: { id: 'direction', type: 'direction' }
        }
        try {
          setFetchingPricing(true);
          const pricingData = await getDefaultPricing(pricingParams);
          if (pricingData?.data?.rates?.length > 0) {
            
            const pricingTagged = {
              ...pricingData.data,
              originPlaceId: origin.place_id || origin.place?.id || origin.google_place_id,
              destinationPlaceId: destination.place_id || destination.place?.id || destination.google_place_id
            };
            //actions.leg.updateField(legIndex, 'pricing', pricingTagged);
            // Notify parent of pricing change
            handlePricingChange(legIndex, pricingTagged);
            setFetchingPricing(false);
          }
        } catch (error) {
          setFetchingPricing(false);
          console.error('Error fetching pricing data:', error);
        }
      }
    }
    fetchPricing();
  }, [currentLeg, memoizedOrigin, memoizedDestination, getDefaultPricing, legIndex]);


  const handleUpdateLegField = useCallback((legIndex, field, value) => {
    actions.leg.updateField(legIndex, field, value);
  }, [actions]);

  const handleAddressChange = (legIndex, addressType, newAddress) => {
    handleUpdateLegField(legIndex, addressType, newAddress);
  };

  const handlePricingChange = useCallback((legIndex, newPricing) => {
    handleUpdateLegField(legIndex, 'pricing', newPricing);

    // if there is no default product selected, add a checked luggage
    // we have to get the product from the pricing data
    if (!helpers.hasSelectedProducts(legIndex)) {
      const luggageProductLine = newPricing?.rates?.find(rate => rate.name === 'Luggage');
      if(luggageProductLine) {
        const defaultProduct = luggageProductLine.products.find(prod => prod.name === 'checked');
        if(defaultProduct) {
          handleUpdateLegField(legIndex, 'selectedProducts', [
            {
              ...defaultProduct,
              quantity: 1,
            },
          ]);
        }
      }
    }

    // if there is no arrivalDate in the leg, lets set one
    // on a monday 2 weeks from today
    if (!helpers.hasLegField(legIndex, 'arrivalDate')) {
      const lastArrival = legIndex  > 0 && currentLeg?.selectedService?.arrival_date;
      const arrivalDate = lastArrival ? calcNextDeliveryDate(new Date(lastArrival)) : calcDefaultDeliveryDate()
      actions.leg.updateFields(legIndex, {
        shipDate: null,
        arrivalDate: arrivalDate,
        deliveryDate: arrivalDate,
      });
    }
  }, [currentLeg, actions, helpers, handleUpdateLegField]);

  const handleServiceSelected = useCallback((legIndex, service) => {
    if(service.service_type === 'deliver_by') {
      // this is a delivery by
      actions.leg.updateFields(legIndex, {
        selectedService: service,
        lastServiceSelectionTime: Date.now(),
        shipDate: service.ship_date,
        // don't set delivery date because its different
        // because of the paddings and will cause a loop
      });
    }
    else
    {
      actions.leg.updateFields(legIndex, {
        selectedService: service,
        lastServiceSelectionTime: Date.now(),
        arrivalDate: service.arrival_date,
        deliveryDate: service.arrival_date,
        // don't set ship date
      });
    } 
  }, [handleUpdateLegField]);

  const handleDeliveryDateChange = useCallback((legIndex, date) => {
    handleUpdateLegField(legIndex, 'deliveryDate', date);
  }, [handleUpdateLegField]);

  const handleShipDateChange = useCallback((legIndex, date) => {
    handleUpdateLegField(legIndex, 'shipDate', date);
  }, [handleUpdateLegField]);

  const handleServicesLoaded = useCallback((legIndex, services) => {
    handleUpdateLegField(legIndex, 'availableServices', services);
  }, [handleUpdateLegField]);

  const removeLeg = useCallback(() => {
    actions.form.removeLeg(legIndex);
  }, [legIndex, actions]);


  const totalLegPrice = useMemo(() => {
    return helpers.getLegTotal(legIndex);
  }, [currentLeg, legIndex]);

  // Theme-based styling
  const isDark = theme === 'dark';

    const tabContentClassName = clsx(
    'transition-all duration-200',
    {
      'bg-slate-800 border-slate-700 hover:border-slate-600': isDark,
      'bg-white border-gray-200 hover:border-gray-300': !isDark
    }
  );

  return (
    <div data-testid={`leg${legIndex}-leg-form`} className={tabContentClassName}>
      {/* Collapsible Header */}
     
      <div className="md:hidden flex flex-row justify-between mb-2">
        <div className="text-sm font-medium p-2">
          {`Leg ${legIndex + 1}:  `}
          {currentLeg.fromAddress?.city && (
            <span className="font-semibold">
              {`${currentLeg?.fromAddress?.city || 'Origin'} to ${currentLeg?.toAddress?.city || 'Destination'}`}
            </span>
          )}
        </div>
        <button
          onClick={removeLeg}
          className="ml-4 text-blue-600 hover:underline"
        >
          <X size={16} />
        </button>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="animate-in fade-in duration-200">
          <div className="flex flex-col md:flex-row gap-6 p-2 md:p-6">
            <div className="flex flex-col gap-6">
              <div className="w-full space-y-2 lg:w-[520px]">
                <LegCard 
                  legIndex={legIndex} 
                  onRemove={() => removeLeg(legIndex)}
                  onAddressChange={handleAddressChange}
                  onPricingChange={handlePricingChange}
                  totalPrice={totalLegPrice}
                  showRemove={false}
                  originRef={originRef}
                  destinationRef={destinationRef}
                />
              </div>
              <div className="space-y-2">
                <ProductSection 
                  legIndex={legIndex} 
                  updatingPricing={pricingLoading}
                  pricingData={pricing}
                />
              </div>
            </div>
            <div className={clsx(
              'w-full lg:w-[520px] rounded-lg',
              {
                'bg-slate-800': isDark,
                'bg-slate-200': !isDark
              }
            )}>
              <DeliverySection 
                legIndex={legIndex}
                blockWeekends={true}
                onSetDeliveryDate={handleDeliveryDateChange}
                onSetShipDate={handleShipDateChange}
                onServicesLoaded={handleServicesLoaded}
                onServiceSelected={handleServiceSelected}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ShippingLegForm.propTypes = {
  legIndex: PropTypes.number.isRequired,
  defaultExpanded: PropTypes.bool,
  originRef: PropTypes.object,
  destinationRef: PropTypes.object
};

export default ShippingLegForm;